'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Home, Grid3X3, FileText, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNativeApp } from '@/lib/native';

interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
  matchPaths?: string[];
}

const navItems: NavItem[] = [
  {
    href: '/',
    label: 'Home',
    icon: <Home className="w-5 h-5" />,
    matchPaths: ['/'],
  },
  {
    href: '/categories',
    label: 'Services',
    icon: <Grid3X3 className="w-5 h-5" />,
    matchPaths: ['/categories', '/documents'],
  },
  {
    href: '/blog',
    label: 'Blog',
    icon: <FileText className="w-5 h-5" />,
    matchPaths: ['/blog'],
  },
  {
    href: '/account',
    label: 'Account',
    icon: <User className="w-5 h-5" />,
    matchPaths: ['/account', '/login', '/register'],
  },
];

export function AuthenticatedFooter() {
  const pathname = usePathname();
  const { isNative } = useNativeApp();

  // Only show bottom nav inside the native Capacitor app
  if (!isNative) return null;

  const isActive = (item: NavItem) => {
    if (item.matchPaths) {
      return item.matchPaths.some((path) => {
        if (path === '/') return pathname === '/';
        return pathname.startsWith(path);
      });
    }
    return pathname === item.href || pathname.startsWith(item.href);
  };

  return (
    <>
      {/* Desktop: Floating centered bottom bar */}
      <nav className="hidden md:flex fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg rounded-full shadow-lg border border-gray-200 dark:border-gray-700 px-2 py-1.5">
        {navItems.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
              )}
            >
              {item.icon}
              <span className={cn(
                'text-sm font-medium',
                active ? 'inline' : 'hidden lg:inline'
              )}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* Mobile: Fixed bottom navigation — same structure as original so safe-area
          padding sits BELOW the 64px icon row, not inside it */}
      <nav className="app-bottom-nav md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border safe-area-bottom">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 flex-1 py-2 transition-colors touch-manipulation',
                  active
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground active:text-primary'
                )}
              >
                <span className={cn(
                  'relative transition-transform',
                  active && 'scale-110'
                )}>
                  {item.icon}
                  {active && (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                  )}
                </span>
                <span className={cn(
                  'text-[10px] font-medium',
                  active && 'text-primary'
                )}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* No spacer needed: MobileLayout's <main> already reserves
          padding-bottom = bottom-nav-height + safe-area-inset-bottom */}
    </>
  );
}
